import { useEffect, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Building2, MapPin, Users, Home, Pencil, FileText, Phone, Smartphone, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { OfficeFormDialog } from "@/components/OfficeFormDialog";
import { ViewToggle, useViewMode } from "@/components/ViewToggle";
import { Pager, SearchBox, usePaged } from "@/components/Pager";

const INFO = [["company_name", "Şirket"], ["chamber_reg_no", "Oda Sicil No"], ["trade_reg_no", "Ticaret Sicil No"], ["activity_no", "Faaliyet No"], ["license_no", "Yetki Belgesi No"]];

export default function OfficesPage() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [mode, setMode] = useViewMode("offices");

  const load = async () => {
    const { data } = await api.get("/offices");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Ofis ve tüm verileri (kullanıcı, portföy, müşteri, finans, araç) kalıcı olarak silinecek. Emin misiniz?")) return;
    await api.delete(`/offices/${id}`);
    load();
  };

  const openLicense = (o) => window.open(`${API}/offices/${o.id}/license?auth=${getToken()}`, "_blank");
  const [oq, setOq] = useState("");
  const paged = usePaged(items, oq);


  return (
    <div className="space-y-6" data-testid="offices-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Yönetim</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Emlak Ofisleri</h1>
          <p className="text-slate-500 mt-1">Bölgesel ofisleri ve kullanıcılarını yönetin.</p>
        </div>
        {hasPerm("OFFICE_CREATE") && <Button data-testid="add-office-btn" onClick={() => setDialog({ office: null })} className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-2" />Yeni Ofis</Button>}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3"><SearchBox value={oq} onChange={setOq} placeholder="Ofis adı, kod, şehir ara..." testid="offices-search" /><ViewToggle mode={mode} onChange={setMode} /></div>

      {mode === "list" && (
        <Card className="overflow-x-auto" data-testid="offices-list-view">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>{["Ofis", "Şirket", "Yetki Belgesi No", "Telefon", "Kullanıcı", "Portföy", "Belge", ""].map((h, i) => <th key={i} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {paged.items.map((o) => (
                <tr key={o.id} data-testid={`office-row-${o.office_code}`} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-2.5"><div className="font-semibold">{o.name}</div><div className="text-xs text-slate-500"><span className="font-mono">{o.office_code}</span> · {o.city}</div></td>
                  <td className="px-4 py-2.5 max-w-[220px] truncate">{o.company_name || <span className="text-amber-600">Eksik</span>}</td>
                  <td className="px-4 py-2.5">{o.license_no || "-"}</td>
                  <td className="px-4 py-2.5 text-xs whitespace-nowrap">{o.phone || "-"}<div>{o.mobile}</div></td>
                  <td className="px-4 py-2.5 font-semibold">{o.user_count}</td>
                  <td className="px-4 py-2.5 font-semibold">{o.portfolio_count}</td>
                  <td className="px-4 py-2.5">{o.license_document ? <Button size="sm" variant="outline" className="h-8" onClick={() => openLicense(o)}><FileText className="w-3.5 h-3.5 text-red-600" /></Button> : <span className="text-xs text-amber-700">Yok</span>}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1">
                      {hasPerm("OFFICE_EDIT") && <Button size="sm" variant="ghost" data-testid={`edit-office-row-${o.office_code}`} onClick={() => setDialog({ office: o })}><Pencil className="w-4 h-4" /></Button>}
                      {hasPerm("OFFICE_DELETE") && <Button size="sm" variant="ghost" onClick={() => handleDelete(o.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {mode === "grid" && <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="offices-grid-view">
        {paged.items.map((o) => (
          <Card key={o.id} data-testid={`office-card-${o.office_code}`} className="p-6 hover:shadow-md transition flex flex-col">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-slate-900 text-emerald-400 flex items-center justify-center">
                <Building2 className="w-6 h-6" />
              </div>
              <div className="flex gap-1">
                {hasPerm("OFFICE_EDIT") && <Button size="sm" variant="ghost" data-testid={`edit-office-${o.office_code}`} onClick={() => setDialog({ office: o })}><Pencil className="w-4 h-4" /></Button>}
                {hasPerm("OFFICE_DELETE") && <Button size="sm" variant="ghost" data-testid={`delete-office-${o.office_code}`} onClick={() => handleDelete(o.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
              </div>
            </div>
            <div className="font-bold text-xl text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{o.name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" />{o.address ? `${o.address}, ` : ""}{o.city}</div>
            <div className="font-mono text-xs bg-slate-100 px-2 py-1 rounded inline-block self-start text-slate-700 mb-4">{o.office_code}</div>

            <dl className="text-xs space-y-1.5 mb-4">
              {INFO.map(([k, l]) => (
                <div key={k} className="flex justify-between gap-3"><dt className="text-slate-500">{l}</dt><dd className="font-medium text-slate-800 text-right truncate">{o[k] || <span className="text-amber-600">Eksik</span>}</dd></div>
              ))}
              <div className="flex justify-between gap-3"><dt className="text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />Sabit</dt><dd className="font-medium">{o.phone || "-"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500 flex items-center gap-1"><Smartphone className="w-3 h-3" />Cep</dt><dd className="font-medium">{o.mobile || "-"}</dd></div>
            </dl>

            {o.license_document ? (
              <Button size="sm" variant="outline" data-testid={`view-license-${o.office_code}`} onClick={() => openLicense(o)} className="mb-4 justify-start">
                <FileText className="w-4 h-4 mr-2 text-red-600" />Yetki Belgesi (PDF)
              </Button>
            ) : (
              <div className="mb-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" />Yetki belgesi yüklenmemiş</div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-3 border-t mt-auto">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-xs text-slate-500 mb-1"><Users className="w-3 h-3" />Kullanıcı</div>
                <div className="font-bold text-lg">{o.user_count}</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-xs text-slate-500 mb-1"><Home className="w-3 h-3" />Portföy</div>
                <div className="font-bold text-lg">{o.portfolio_count}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>}
      <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} testid="offices-pager" />

      <OfficeFormDialog open={!!dialog} office={dialog?.office} onClose={() => setDialog(null)} onSaved={load} />
    </div>
  );
}
